import React, { useEffect } from 'react';
import { sdk } from '@farcaster/miniapp-sdk';
import { ModalCloseButton } from './ModalCloseButton';

const CREATOR_FID = 379342;
const CREATOR_URL = 'https://farcaster.xyz/maks1';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleCreatorLink = React.useCallback(async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const href = event.currentTarget.href || CREATOR_URL;

    try {
      const isMiniApp = await sdk.isInMiniApp();
      if (isMiniApp) {
        try {
          await sdk.actions.viewProfile({ fid: CREATOR_FID });
          await sdk.actions.close();
          return;
        } catch (sdkError) {
          console.warn('Failed to open profile via SDK, falling back to browser:', sdkError);
        }
      }
    } catch (error) {
      console.warn('Mini app detection failed, falling back to browser:', error);
    }

    if (typeof window !== 'undefined') {
      window.open(href, '_blank', 'noopener');
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-slate-900 to-purple-900 z-50">
      <ModalCloseButton onClose={onClose} className="text-white/80" />

      <div className="h-full flex flex-col px-4 pt-12 pb-8 overflow-y-auto">
        <h1 className="crypto-heading text-white text-xl text-center mb-6">
          What is Stradamus?
        </h1>

        <div className="max-w-2xl mx-auto space-y-4 text-white">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-purple-400/20">
            <p className="text-white/90 text-base leading-relaxed text-center">
              Stradamus is crypto tarot where you ask the universe a question and draw 3 cards that might just reveal some cosmic insights about your future. Think of it as your digital crystal ball - with a healthy dose of mystery and maybe a sprinkle of luck! 🔮
            </p>
          </div>

          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-purple-400/20">
            <h2 className="text-purple-300 font-semibold mb-4 text-lg text-center">How It Works</h2>
              <div className="space-y-4 text-white/90">
                <div>
                  <strong className="text-purple-200">The Deck:</strong> 50 crypto tarot cards, or 78 classic tarot cards (Rider-Waite), each with its own energy and meaning.
                </div>
              <div>
                <strong className="text-purple-200">The Process:</strong> Every reading shuffles the full deck and randomly presents 8 cards. You choose 3 that call to you - trust your instincts!
              </div>
              <div>
                <strong className="text-purple-200">The Insight:</strong> Each card reveals its name and a personalized insight to help guide your path.
              </div>
              <div>
                <strong className="text-purple-200">Magic Insight:</strong> An optional AI reading that reacts to your question and the spread, delivering a premium interpretation once on-chain payment lands.
              </div>
              <div>
                <strong className="text-purple-200">Share:</strong> Love your reading? Share it with friends and spread the cosmic vibes.
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-red-300 font-semibold mb-2">Not Financial Advice!</h2>
            <p className="text-red-200/90 text-sm">
              This is for entertainment purposes only.
            </p>
          </div>

          <div className="text-center">
            <span className="text-white/70 text-sm">Questions - </span>
            <a
              href={CREATOR_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCreatorLink}
              className="text-purple-200 hover:text-purple-100 transition-colors text-sm"
            >
              maks1
            </a>
          </div>

          <div className="h-8"></div>
        </div>
      </div>
    </div>
  );
};
